# There are small discrepencies between products stored by Luther and products
# returned from sourcing via the microservice. Until these can be aligned the
# former will be referred to as 'ProtectionProduct' and the latter will be
# referred to as 'SolutionProtectionProduct'

enum SolutionType {
  MULTIPLE_BENEFITS_IN_MULTIPLE_PRODUCTS
  MULTIPLE_BENEFITS_IN_ONE_PRODUCT
  ONE_BENEFIT_PER_PRODUCT
  ALL
}

enum SolutionSourcingStatus {
  PENDING
  COMPLETE
  UNAVAILABLE
  ERROR
}

enum SolutionProductType {
  PRODUCT_TYPE_INVALID_PRODUCT_TYPE
  PRODUCT_TYPE_TERM
  PRODUCT_TYPE_LEVEL_TERM
  PRODUCT_TYPE_DECREASING_TERM
  PRODUCT_TYPE_INCOME_PROTECTION
  PRODUCT_TYPE_FAMILY_INCOME_BENEFIT
  PRODUCT_TYPE_WHOLE_OF_LIFE
  PRODUCT_TYPE_GROUP_LIFE
  PRODUCT_TYPE_GROUP_PMI
  PRODUCT_TYPE_GROUP_ASU
  PRODUCT_TYPE_GROUP_CIC
  PRODUCT_TYPE_GROUP_IP
  PRODUCT_TYPE_RELEVANT_LIFE
  PRODUCT_KEY_MAN
  PRODUCT_TYPE_PMI
  PRODUCT_TYPE_SHAREHOLDER_PROTECTION
  PRODUCT_TYPE_GROUP_PRIVATE_MEDICAL_INSURANCE
  PRODUCT_TYPE_EXECUTIVE_IP
}

enum SolutionPremiumFrequency {
  INVALID_FREQUENCY
  MONTHLY
  ANNUAL
}

enum IntegratorName {
  INVALID_INTEGRATOR_NAME
  IPIPELINE
  IRESS
}

enum CommissionOptionType {
  INVALID
  FULL
  MODIFIED
  NIL
}

#
# Enums
#

# Response for the initial sourcing request
type RunSolutionSourcingResponse {
  location: String!
  product_benefits: SolutionProductBenefit!
  source_response: [ProtectionSourceResponse!]
  exception: Exception
}

type ProtectionSourceResponse {
  solution_type: SolutionType!
  sourced_solution_products: [SourcedSolutionProduct!]!
}

type SourcedSolutionProduct {
  source_id: String
  product_benefit_ids: [String!]
  exception: Exception
}

# Response for the polled responses after sourcing is initiated
type GetSolutionSourcingResultsResponse {
  id: String
  product_benefits: SolutionProductBenefit!
  solutions: [Solution!]!
  # WIP
  # generated_at: String!
  # signature: String!
  exception: Exception
}

type Solution {
  solution_type: SolutionType!
  nb_product_quotes_requested: Int
  nb_product_quotes_returned: Int
  product_benefit_groups: [SolutionProductBenefitGroup!]
  products: [SolutionProtectionProduct!]
}

type SolutionProtectionProduct {
  status: SolutionSourcingStatus
  expires_at: String
  errors: [String!]
  warnings: [String!]
  product_price_monthly: String
  provider_name: String
  product_name: String
  benefits: SolutionProductBenefit
  can_apply_at_provider_extranet: Boolean
  product_benefit_group_id: String
  integrator_metadata: IntegratorMetadata
  org_provider_reference: String
  sourced_with_bmi: Boolean
}

type IntegratorMetadata {
  integrator_name: String
  integrator_product_reference: String
  integrator_quote_reference: String
  integrator_provider_reference: String
}

type ProductAddons {
  fracture_cover_client_ids: [String!]
  waiver_of_premium_client_ids: [String!]
  include_total_permanent_disability: Boolean
}

type EmployerFundingOptions {
  include_employer_ni_contributions: Boolean
  include_employer_pension_contributions: Boolean
  employer_pension_contribution_amount: String
}

type ProductAddonsWol {
  waiver_of_premium_client_ids: [String!]
}

type SolutionProductBenefit {
  product_type_term: [SolutionProductBenefitItem!]
  product_type_family_income_benefit: [SolutionProductBenefitItem!]
  product_type_whole_of_life: [SolutionProductBenefitItem!]
  product_type_income_protection: [SolutionProductBenefitItem!]
  product_type_other: [SolutionProductBenefitItem!]
  product_type_group_life: [SolutionProductBenefitGroupItem!]
  product_type_group_pmi: [SolutionProductBenefitGroupItem!]
  product_type_group_asu: [SolutionProductBenefitGroupItem!]
  product_type_group_cic: [SolutionProductBenefitGroupItem!]
  product_type_group_ip: [SolutionProductBenefitGroupItem!]
  product_type_relevant_life: [SolutionProductBenefitItem!]
  product_type_executive_ip: [SolutionProductBenefitItem!]
  product_key_man: [SolutionProductBenefitGroupItem!]
  product_type_pmi: [SolutionProductBenefitItemPMI!]
  product_type_shareholder_protection: [SolutionProductBenefitGroupItem!]
  product_type_core_accident_sickness: [SolutionProductBenefitItemMetLife!]
  product_type_child_accident_sickness: [SolutionProductBenefitItemMetLife!]
  product_type_sports_lifestyle: [SolutionProductBenefitItemMetLife!]
  product_type_specialist_healthcare_cover: [SolutionProductBenefitItemMetLife!]
  product_type_private_medical_insurance: [SolutionProductBenefitItemPrivateMedicalInsurance!]
  product_type_group_private_medical_insurance: [SolutionProductBenefitItemPrivateMedicalInsurance!]
  product_type_employee_benefit: [SolutionProductBenefitEmployeeBenefit!]
}

type SolutionProductBenefitItemPMI {
  client_ids: [String!]
  cover_amount_frequency: Frequency
  cover_amount: String
  term_years: Int
  excess_amount: String
  underwriting_level: UnderwritingLevel
}

type SolutionProductBenefitGroupItem {
  client_ids: [String!]
  cover_amount_frequency: Frequency
  cover_amount: String
  premium_type: PremiumType
  term_years: Int
  term_age: Int
}

type SolutionProductBenefitItemMetLife {
  client_ids: [String!]
  cost_amount: String
  cost_amount_frequency: Frequency
  units_of_cover: CoverUnits
}

type SolutionProductBenefitItemPrivateMedicalInsurance {
  client_ids: [String!]
  private_gp_cover: Boolean
  mental_health: Boolean
  additional_notes: String
  travel_international: Boolean
  six_week_option: Boolean
  excess_amount: String
  underwriting_level: UnderwritingLevel
  outpatient_cover: String
  hospital_list: HospitalList
  cancer_cover: Boolean
  dental_and_optical: Boolean
  therapies: Boolean
  physiotherapy: Boolean
  outpatient_full_cover: Boolean
}

type SolutionProductBenefitEmployeeBenefit {
  client_ids: [String!]
  additional_notes: String
}

type SolutionProductBenefitItem {
  additional_cover_amount: String
  additional_deferral_period_days: Int
  client_ids: [String!]
  cover_amount_depreciation_rate: Int
  cover_amount: String
  cover_amount_type: CoverAmountType
  deferral_period_days: Int
  include_limited_benefit_payment_period: Boolean
  include_low_start: Boolean
  indexation_percentage: Int
  indexation_type: IndexationType
  payout_type: PayoutType
  premium_frequency: SolutionPremiumFrequency
  premium_type: PremiumType
  product_add_ons: ProductAddons
  product_benefit_id: ID
  product_type_other_name: String
  product_type: SolutionProductType!
  term_years: Int
  term_age: Int
  age_banded: Boolean
  limited_payment_plan: Boolean
  limited_payment_plan_term: Int
  cover_amount_life: String
  cover_amount_cic: String
  cover_amount_life_or_earlier_cic: String
  employer_funding_options: EmployerFundingOptions
}

type SolutionProductBenefitGroup {
  product_benefit_group_id: String
  nb_product_quotes_requested: Int
  nb_product_quotes_returned: Int
  status: SolutionSourcingStatus
  product_benefit_ids: [String!]
}

#
# Inputs
#

input SolutionProtectionProductInput {
  benefits: SolutionProductBenefitInput
  status: SolutionSourcingStatus
  expires_at: String
  errors: [String!]
  warnings: [String!]
  product_price_monthly: String
  provider_name: String
  product_name: String
  can_apply_at_provider_extranet: Boolean
  product_benefit_group_id: String
  integrator_metadata: IntegratorMetadataInput
  org_provider_reference: String
}

input IntegratorMetadataInput {
  integrator_name: String
  integrator_product_reference: String
  integrator_quote_reference: String
  integrator_provider_reference: String
}

input SolutionProductBenefitGroupInput {
  product_benefit_group_id: String
  nb_product_quotes_requested: Int
  nb_product_quotes_returned: Int
  status: SolutionSourcingStatus
  product_benefit_ids: [String!]
}

input SolutionInput {
  solution_type: SolutionType!
  nb_product_quotes_requested: Int
  nb_product_quotes_returned: Int
  product_benefit_groups: [SolutionProductBenefitGroupInput]
  products: [SolutionProtectionProductInput!]
}

input SaveToCaseInput {
  id: String
  case_id: String!
  solution_type: SolutionType!
  integrator_quote_references: [String]
  status: ProtectionProductStatus
}

type SaveToCaseOutput {
  product_ids: [String]
}

input RunSolutionSourcingInput {
  solution_type: SolutionType!
  clients: [ProductEstimateClientInput!]!
  product_benefits: ProductBenefitInput!
  case_id: String
  options: CommissionOptionsInput!
}

input CommissionOptionsInput {
  percentage: Int
  type: CommissionOptionType
  indemnity_required: Boolean
}

input SolutionProductBenefitInput {
  product_type_term: [SolutionProductBenefitItemInput!]
  product_type_family_income_benefit: [SolutionProductBenefitItemInput!]
  product_type_whole_of_life: [SolutionProductBenefitItemInput!]
  product_type_income_protection: [SolutionProductBenefitItemInput!]
  product_type_other: [SolutionProductBenefitItemInput!]
  product_type_relevant_life: [SolutionProductBenefitItemInput!]
  product_type_executive_ip: [SolutionProductBenefitItemInput!]
}

input SolutionProductBenefitItemInput {
  additional_cover_amount: String
  additional_deferral_period_days: Int
  client_ids: [String!]!
  cover_amount_depreciation_rate: Int
  cover_amount_life: String
  cover_amount_cic: String
  cover_amount_life_or_earlier_cic: String
  cover_amount: String
  cover_amount_type: CoverAmountType
  deferral_period_days: Int
  include_limited_benefit_payment_period: Boolean
  include_low_start: Boolean
  indexation_percentage: Int
  indexation_type: IndexationType
  indexation: String
  payout_type: PayoutType
  premium_frequency: SolutionPremiumFrequency
  premium_type: PremiumType
  product_add_ons: ProductAddonsInput
  product_benefit_id: String
  product_type_other_name: String
  product_type: SolutionProductType!
  term_years: Int
  term_age: Int
  employer_funding_options: EmployerFundingOptionsInput
}

input EmployerFundingOptionsInput {
  include_employer_ni_contributions: Boolean
  include_employer_pension_contributions: Boolean
  employer_pension_contribution_amount: String
}

input ProductAddonsInput {
  fracture_cover_client_ids: [String!]
  waiver_of_premium_client_ids: [String!]
  include_total_permanent_disability: Boolean
}

input ProductAddonsWolInput {
  waiver_of_premium_client_ids: [String!]
}
